import { DateTime } from 'luxon'

const DEFAULT_TIME_ZONE = 'UTC'
const LOCAL_DATE_FORMAT = 'yyyy-LL-dd'
const LOCAL_TIME_FORMAT = 'HH:mm'
const LOCAL_DATE_TIME_FORMAT = "yyyy-LL-dd'T'HH:mm"
const PREFERRED_US_TIME_ZONES = [
  { name: 'America/New_York', fallbackAbbreviation: 'ET', label: 'Eastern Time' },
  { name: 'America/Chicago', fallbackAbbreviation: 'CT', label: 'Central Time' },
  { name: 'America/Denver', fallbackAbbreviation: 'MT', label: 'Mountain Time' },
  { name: 'America/Los_Angeles', fallbackAbbreviation: 'PT', label: 'Pacific Time' }
]

const attr = (record, key) => record?.attributes?.[key] ?? null

const asArray = (json) => (Array.isArray(json?.data) ? json.data : (json?.data ? [json.data] : []))

const humanizeTimeZoneName = (name) => String(name || '')
  .split('/')
  .map((part) => part.replace(/_/g, ' '))
  .join(' / ')

const currentTimeZoneAbbreviation = (name, fallback = '') => {
  const dateTime = DateTime.now().setZone(name)
  return (dateTime.isValid ? dateTime.offsetNameShort : '') || fallback
}

const preferredUsOptionMap = new Map(
  PREFERRED_US_TIME_ZONES.map((option, index) => [
    option.name,
    {
      ...option,
      priority: index
    }
  ])
)

export const formatTimeZoneOptionLabel = (name, label = name) => {
  const normalizedName = String(name || '').trim()
  if (!normalizedName) return ''

  const preferredUsOption = preferredUsOptionMap.get(normalizedName)
  if (preferredUsOption) {
    const abbreviation = currentTimeZoneAbbreviation(normalizedName, preferredUsOption.fallbackAbbreviation)
    return `${abbreviation} - ${preferredUsOption.label}`
  }

  const abbreviation = currentTimeZoneAbbreviation(normalizedName)
  const humanizedName = humanizeTimeZoneName(label || normalizedName)
  return abbreviation ? `${abbreviation} - ${humanizedName}` : humanizedName
}

export const getBrowserTimeZone = () => {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return typeof browserTimeZone === 'string' && browserTimeZone ? browserTimeZone : DEFAULT_TIME_ZONE
}

export const extractTimeZoneOptions = (json) => asArray(json)
  .map((record) => {
    const name = attr(record, 'name') || record?.id || ''
    if (!name) return null

    const preferredUsOption = preferredUsOptionMap.get(name)
    if (preferredUsOption) {
      return {
        name,
        label: formatTimeZoneOptionLabel(name, preferredUsOption.label),
        priority: preferredUsOption.priority,
        isPreferredUs: true
      }
    }

    return {
      name,
      label: formatTimeZoneOptionLabel(name, attr(record, 'label') || name),
      priority: 1000,
      isPreferredUs: false
    }
  })
  .filter(Boolean)
  .sort((left, right) => {
    if (left.priority !== right.priority) return left.priority - right.priority
    return left.label.localeCompare(right.label)
  })

export const splitTimeZoneOptions = (options = []) => ({
  preferred: options.filter((option) => option?.isPreferredUs),
  others: options.filter((option) => !option?.isPreferredUs)
})

export const resolvePreferredTimeZone = ({
  explicitTimeZone,
  fallbackTimeZones = [],
  options = []
}) => {
  const candidates = [explicitTimeZone, ...fallbackTimeZones, DEFAULT_TIME_ZONE]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  if (!Array.isArray(options) || options.length === 0) return candidates[0] || DEFAULT_TIME_ZONE

  const availableTimeZones = new Set(options.map((option) => option?.name).filter(Boolean))
  const matchedCandidate = candidates.find((candidate) => availableTimeZones.has(candidate))

  return matchedCandidate || options[0]?.name || DEFAULT_TIME_ZONE
}

export const venueLocalInputToUtcIso = ({ localDateTime, timeZone }) => {
  if (!localDateTime || !timeZone) return null
  const dateTime = DateTime.fromFormat(localDateTime, LOCAL_DATE_TIME_FORMAT, {
    zone: timeZone,
    setZone: true
  })
  if (!dateTime.isValid) return null
  return dateTime.toUTC().toISO({ suppressMilliseconds: true })
}

export const utcIsoToVenueLocalInput = ({ scheduledAt, timeZone }) => {
  if (!scheduledAt || !timeZone) return ''
  const dateTime = DateTime.fromISO(scheduledAt, { zone: 'utc' }).setZone(timeZone)
  if (!dateTime.isValid) return ''
  return dateTime.toFormat(LOCAL_DATE_TIME_FORMAT)
}

export const splitVenueLocalInput = ({ localDateTime }) => {
  if (!localDateTime) return { day: '', time: '' }
  const dateTime = DateTime.fromFormat(localDateTime, LOCAL_DATE_TIME_FORMAT)
  if (!dateTime.isValid) return { day: '', time: '' }
  return {
    day: dateTime.toFormat(LOCAL_DATE_FORMAT),
    time: dateTime.toFormat(LOCAL_TIME_FORMAT)
  }
}

export const venueLocalPartsToInput = ({ day, time, fallbackDay = '' }) => {
  const resolvedDay = String(day || fallbackDay || '').trim()
  const resolvedTime = String(time || '').trim()

  if (!resolvedDay && !resolvedTime) return ''
  if (!resolvedDay || !resolvedTime) return null

  const dateTime = DateTime.fromFormat(`${resolvedDay}T${resolvedTime}`, LOCAL_DATE_TIME_FORMAT)
  if (!dateTime.isValid) return null
  return dateTime.toFormat(LOCAL_DATE_TIME_FORMAT)
}

export const utcIsoToVenueLocalParts = ({ scheduledAt, timeZone }) => (
  splitVenueLocalInput({
    localDateTime: utcIsoToVenueLocalInput({ scheduledAt, timeZone })
  })
)

export const parseVenueLocalDate = (value) => {
  if (!value) return null
  const dateTime = DateTime.fromFormat(String(value), LOCAL_DATE_FORMAT)
  return dateTime.isValid ? dateTime.startOf('day') : null
}

export const parseVenueLocalTime = (value) => {
  if (!value) return null
  const dateTime = DateTime.fromFormat(String(value), LOCAL_TIME_FORMAT)
  return dateTime.isValid ? dateTime : null
}

export const formatVenueLocalDate = (value) => (
  value && value.isValid ? value.toFormat(LOCAL_DATE_FORMAT) : ''
)

export const formatVenueLocalTime = (value) => (
  value && value.isValid ? value.toFormat(LOCAL_TIME_FORMAT) : ''
)

const scheduledInVenueZone = ({ scheduledAt, timeZone }) => {
  if (!scheduledAt) return null
  const normalizedTimeZone = timeZone || DEFAULT_TIME_ZONE
  const dateTime = DateTime.fromISO(scheduledAt, { zone: 'utc' }).setZone(normalizedTimeZone)
  return dateTime.isValid ? dateTime : null
}

export const formatVenueScheduledDate = ({ scheduledAt, timeZone, fallback = 'Not set', compact = false }) => {
  const dateTime = scheduledInVenueZone({ scheduledAt, timeZone })
  if (!dateTime) return fallback
  return dateTime.toFormat(compact ? 'ccc, MMM d, yyyy' : 'EEEE, MMMM d, yyyy')
}

export const formatVenueScheduledTime = ({ scheduledAt, timeZone, fallback = 'Not set' }) => {
  const dateTime = scheduledInVenueZone({ scheduledAt, timeZone })
  if (!dateTime) return fallback
  return dateTime.toFormat('h:mm a ZZZZ')
}

export const formatVenueScheduledSummary = ({ scheduledAt, timeZone, fallback = 'Date: TBD' }) => {
  const dateTime = scheduledInVenueZone({ scheduledAt, timeZone })
  if (!dateTime) return fallback
  return `Date: ${dateTime.toFormat('MMM d, yyyy, h:mm a ZZZZ')}`
}

export const formatVenueScheduledDateTime = ({ scheduledAt, timeZone, fallback = 'Not set' }) => {
  const dateTime = scheduledInVenueZone({ scheduledAt, timeZone })
  if (!dateTime) return fallback
  return dateTime.toFormat('MMM d, yyyy, h:mm a ZZZZ')
}

const formatVenueLowerTime = (dateTime) => dateTime.toFormat('h:mma').toLowerCase()

export const formatVenueScheduleDetailBlocks = ({
  scheduledAt,
  endsAt,
  timeZone,
  fallback = 'Not set',
  compactDates = false
}) => {
  const startsAt = scheduledInVenueZone({ scheduledAt, timeZone })
  if (!startsAt) {
    return [
      {
        label: 'Date',
        lines: [fallback]
      }
    ]
  }

  /** Abbreviated weekday + month (e.g. Fri, May 29, 2026) for compact layouts */
  const dateLineFormat = compactDates ? 'ccc, MMM d, yyyy' : 'ccc, MMMM d, yyyy'
  const multiDayDateLineFormat = compactDates ? 'ccc, MMM d, yyyy' : 'EEEE, MMMM d, yyyy'

  const endsAtDateTime = scheduledInVenueZone({ scheduledAt: endsAt, timeZone })
  if (!endsAtDateTime) {
    return [
      {
        label: 'Date',
        lines: [startsAt.toFormat(dateLineFormat)]
      },
      {
        label: 'Time',
        lines: [`${formatVenueLowerTime(startsAt)}${startsAt.offsetNameShort ? ` ${startsAt.offsetNameShort}` : ''}`]
      }
    ]
  }

  if (startsAt.hasSame(endsAtDateTime, 'day')) {
    return [
      {
        label: 'Date',
        lines: [startsAt.toFormat(dateLineFormat)]
      },
      {
        label: 'Time',
        lines: [`${formatVenueLowerTime(startsAt)} to ${formatVenueLowerTime(endsAtDateTime)}`]
      }
    ]
  }

  return [
    {
      label: 'Start Date',
      lines: [
        startsAt.toFormat(multiDayDateLineFormat),
        `${formatVenueLowerTime(startsAt)}${startsAt.offsetNameShort ? ` ${startsAt.offsetNameShort}` : ''}`
      ]
    },
    {
      label: 'End Date',
      lines: [
        endsAtDateTime.toFormat(multiDayDateLineFormat),
        `${formatVenueLowerTime(endsAtDateTime)}${endsAtDateTime.offsetNameShort ? ` ${endsAtDateTime.offsetNameShort}` : ''}`
      ]
    }
  ]
}
